<template>
  <div class="meet-room">

    <!-- ── LOBBY ─────────────────────────────────────────────────────────── -->
    <LobbyScreen
      v-if="phase === VIDEO_PHASES.LOBBY"
      :room-id="roomId"
      :default-name="defaultName"
      :is-logged-in="$store.getters.loggedIn"
      @join="handleJoin"
    />

    <!-- ── CONNECTING / RECONNECTING ─────────────────────────────────────── -->
    <div v-else-if="phase === VIDEO_PHASES.CONNECTING || phase === VIDEO_PHASES.RECONNECTING" class="meet-overlay">
      <v-progress-circular indeterminate color="blue-lighten-2" size="56" width="4" />
      <p class="meet-overlay__label">
        {{ phase === VIDEO_PHASES.RECONNECTING ? 'Reconnecting…' : connectingLabel }}
      </p>
    </div>

    <!-- ── CONNECTED ──────────────────────────────────────────────────────── -->
    <template v-else-if="phase === VIDEO_PHASES.CONNECTED">
      <TopBar
        :room-name="roomId"
        :participant-count="totalPeers"
        :is-recording="isRecording"
        :start-time="meetingStartedAt"
        :share-link="shareLink"
        :is-admin="isAdmin"
        :show-participants="showParticipants"
        :show-waiting-room="showWaitingRoom"
        :waiting-guest-count="waitingGuestCount"
        @toggle-participants="showParticipants = !showParticipants"
        @toggle-waiting-room="showWaitingRoom = !showWaitingRoom"
      />

      <!-- Pinned layout: main tile + sidebar strip -->
      <div v-if="pinnedKey && pinnedTile" class="meet-pinned-layout">
        <!-- Main area: the pinned tile -->
        <div class="pinned-main">
          <VideoTile
            v-if="pinnedTile.type === 'screen'"
            :stream="pinnedTile.stream"
            :name="pinnedTile.name"
            :is-muted="false"
            :is-video-off="false"
            :is-speaking="false"
            :is-screen-share="true"
            :is-pinned="true"
            :show-pin-button="true"
            :sink-id="speakerDeviceId"
            @pin="onPin(IDENTITY_PREFIXES.SCREEN_SHARE + pinnedTile.identity)"
            @sink-id-error="handleSinkIdError"
          />
          <VideoTile
            v-else
            :stream="pinnedTile.stream"
            :name="pinnedTile.name"
            :is-muted="pinnedTile.audioMuted"
            :is-video-off="pinnedTile.videoMuted"
            :is-speaking="activeSpeakers.includes(pinnedTile.identity)"
            :is-pinned="true"
            :show-pin-button="true"
            :sink-id="speakerDeviceId"
            @pin="onPin(pinnedTile.identity)"
            @sink-id-error="handleSinkIdError"
          />
        </div>

        <!-- Sidebar: all non-pinned tiles stacked vertically -->
        <div class="pinned-sidebar">
          <!-- Local tile always in sidebar -->
          <VideoTile
            :stream="localStream"
            :name="localName"
            :is-muted="localAudioMuted"
            :is-video-off="localVideoMuted"
            :is-speaking="activeSpeakers.includes(peerId)"
            :is-local="true"
            :is-admin="isAdmin"
          />
          <!-- Non-pinned remote tiles -->
          <VideoTile
            v-for="tile in remoteTiles.filter(t => !t.isPinned)"
            :key="tile.identity"
            :stream="tile.stream"
            :name="tile.name"
            :is-muted="tile.audioMuted"
            :is-video-off="tile.videoMuted"
            :is-speaking="activeSpeakers.includes(tile.identity)"
            :is-pinned="false"
            :show-pin-button="true"
            :sink-id="speakerDeviceId"
            @pin="onPin(tile.identity)"
            @sink-id-error="handleSinkIdError"
          />
          <!-- Non-pinned screen share tiles -->
          <VideoTile
            v-for="identity in screenShareParticipants.filter(id => pinnedKey !== IDENTITY_PREFIXES.SCREEN_SHARE + id)"
            :key="'ss-' + identity"
            :stream="screenShareStreams[identity] || null"
            :name="screenShareName(identity)"
            :is-muted="false"
            :is-video-off="false"
            :is-speaking="false"
            :is-screen-share="true"
            :is-pinned="false"
            :show-pin-button="true"
            :sink-id="speakerDeviceId"
            @pin="onPin(IDENTITY_PREFIXES.SCREEN_SHARE + identity)"
            @sink-id-error="handleSinkIdError"
          />
        </div>
      </div>

      <!-- Normal video grid (no pin active) -->
      <div v-else class="meet-grid" :class="gridClass">
        <!-- Local tile -->
        <VideoTile
          :stream="localStream"
          :name="localName"
          :is-muted="localAudioMuted"
          :is-video-off="localVideoMuted"
          :is-speaking="activeSpeakers.includes(peerId)"
          :is-local="true"
          :is-admin="isAdmin"
        />

        <!-- Remote tiles -->
        <VideoTile
          v-for="tile in remoteTiles"
          :key="tile.identity"
          :stream="tile.stream"
          :name="tile.name"
          :is-muted="tile.audioMuted"
          :is-video-off="tile.videoMuted"
          :is-speaking="activeSpeakers.includes(tile.identity)"
          :is-pinned="tile.isPinned"
          :show-pin-button="true"
          :sink-id="speakerDeviceId"
          @pin="onPin(tile.identity)"
          @sink-id-error="handleSinkIdError"
        />

        <!-- Screen share tiles -->
        <VideoTile
          v-for="identity in screenShareParticipants"
          :key="'ss-' + identity"
          :stream="screenShareStreams[identity] || null"
          :name="screenShareName(identity)"
          :is-muted="false"
          :is-video-off="false"
          :is-speaking="false"
          :is-screen-share="true"
          :is-pinned="pinnedKey === IDENTITY_PREFIXES.SCREEN_SHARE + identity"
          :show-pin-button="true"
          :sink-id="speakerDeviceId"
          @pin="onPin(IDENTITY_PREFIXES.SCREEN_SHARE + identity)"
          @sink-id-error="handleSinkIdError"
        />

        <!-- Waiting placeholder -->
        <div v-if="totalPeers === 1" class="meet-waiting">
          <v-icon size="48" color="grey-darken-1">mdi-account-multiple-outline</v-icon>
          <p>Waiting for others to join…</p>
          <p class="meet-waiting__hint">Share: <strong>{{ shareLink }}</strong></p>
        </div>
      </div>

      <ControlBar
        :audio-muted="localAudioMuted"
        :video-muted="localVideoMuted"
        :is-screen-sharing="isScreenSharing"
        :is-recording="isRecording"
        :is-admin="isAdmin"
        :speaker-device-id="speakerDeviceId"
        @toggle-audio="toggleAudio"
        @toggle-video="toggleVideo"
        @toggle-screen-share="toggleScreenShare"
        @toggle-recording="toggleRecording"
        @change-speaker="changeSpeaker"
        @leave="leaveCall"
        @end-meeting="endMeeting"
      />

      <v-snackbar v-model="speakerErrorSnackbar" color="error" timeout="4000">
        Could not switch speaker device. Reverted to the previous one.
      </v-snackbar>

      <ParticipantDrawer
        v-model="showParticipants"
        :participants="participantList"
        :is-admin="isAdmin"
        @mute="onAdminMute"
        @kick="onAdminKick"
      />

      <WaitingRoomPanel
        v-if="isAdmin && showWaitingRoom"
        :guests="waitingGuests"
        @admit="onAdmitGuest"
        @deny="onDenyGuest"
        @close="showWaitingRoom = false"
      />

      <!-- Recording-active leave alert (host only) -->
      <v-dialog v-model="showRecordingLeaveAlert" max-width="380" persistent>
        <v-card color="#1e1e1e" rounded="lg">
          <v-card-text class="pt-5 pb-2 text-center">
            <v-icon size="40" color="error" class="mb-3">mdi-record-circle</v-icon>
            <div style="font-weight:600; font-size:1rem; color:#fff; margin-bottom:8px;">Recording in progress</div>
            <div style="font-size:0.85rem; color:#aaa;">Please stop the recording before leaving the meeting.</div>
          </v-card-text>
          <v-card-actions class="justify-center pb-4">
            <v-btn color="primary" variant="tonal" @click="showRecordingLeaveAlert = false">Got it</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </template>

    <!-- ── WAITING FOR ADMISSION ─────────────────────────────────────────────── -->
    <div v-else-if="phase === VIDEO_PHASES.WAITING" class="meet-overlay">
      <v-progress-circular indeterminate color="blue-lighten-2" size="56" width="4" />
      <p class="meet-overlay__label">Waiting for the host to admit you…</p>
      <p class="meet-overlay__sub">Joining as <strong>{{ localName }}</strong></p>
      <v-btn variant="text" color="grey" class="mt-4" @click="leaveCall">Cancel</v-btn>
    </div>

    <!-- ── DENIED ─────────────────────────────────────────────────────────────── -->
    <div v-else-if="phase === VIDEO_PHASES.DENIED" class="meet-overlay">
      <v-icon size="56" color="error">mdi-account-cancel</v-icon>
      <p class="meet-overlay__label">You were not admitted</p>
      <p class="meet-overlay__sub">The host declined your request to join.</p>
      <v-btn color="blue" class="mt-4" @click="goBack">Go back</v-btn>
    </div>

    <!-- ── ENDED ───────────────────────────────────────────────────────────── -->
    <div v-else-if="phase === VIDEO_PHASES.ENDED" class="meet-overlay">
      <v-icon size="56" color="grey">mdi-video-off</v-icon>
      <p class="meet-overlay__label">Meeting ended</p>
      <p class="meet-overlay__sub">The host has ended this meeting.</p>
      <v-btn color="blue" class="mt-4" @click="goBack">Return home</v-btn>
    </div>

    <!-- ── ERROR ───────────────────────────────────────────────────────────── -->
    <div v-else-if="phase === VIDEO_PHASES.ERROR" class="meet-overlay">
      <v-icon size="56" color="error">mdi-alert-circle-outline</v-icon>
      <p class="meet-overlay__label">{{ error || 'Something went wrong' }}</p>
      <v-btn color="blue" class="mt-4" @click="goBack">Go back</v-btn>
    </div>

  </div>
</template>

<script>
import { markRaw } from 'vue';
import {
  Room,
  RoomEvent,
  Track,
  DisconnectReason,
  ParticipantEvent,
} from 'livekit-client';
import { mapGetters } from 'vuex';

import VideoService from '../services/videoService.js';
import EventBus from '../eventBus';
import {
  VIDEO_PHASES,
  CONNECTION_STATUS,
  DATA_MESSAGE_TYPES,
  IDENTITY_PREFIXES,
  ROUTE_PATHS,
  ROOM_STATUSES,
  PARTICIPANT_ROLES,
} from '../constants/video';
import LobbyScreen from '../components/Video/LobbyScreen.vue';
import TopBar from '../components/Video/TopBar.vue';
import VideoTile from '../components/Video/VideoTile.vue';
import ControlBar from '../components/Video/ControlBar.vue';
import ParticipantDrawer from '../components/Video/ParticipantDrawer.vue';
import WaitingRoomPanel from '../components/Video/WaitingRoomPanel.vue';

export default {
  name: 'VideoRoom',

  components: { LobbyScreen, TopBar, VideoTile, ControlBar, ParticipantDrawer, WaitingRoomPanel },

  data() {
    return {
      VIDEO_PHASES,       // exposed for template v-if comparisons
      IDENTITY_PREFIXES,  // exposed for template 'ss:' usages
      phase: VIDEO_PHASES.LOBBY,  // 'lobby' | 'connecting' | 'connected' | 'reconnecting' | 'error' | 'ended'
      room: null,              // markRaw LiveKit Room
      localStream: null,       // markRaw MediaStream (local camera)
      remoteMediaStreams: {},  // { [identity]: markRaw MediaStream }
      screenShareStreams: {},  // { [identity]: markRaw MediaStream }
      showParticipants: false,
      showWaitingRoom: false,
      showRecordingLeaveAlert: false,
      localName: '',
      speakerDeviceId: null,
      previousSpeakerDeviceId: null,
      speakerErrorSnackbar: false,
      pendingJoinOptions: null, // stores join options while guest is waiting for admission
      _publishPending: null,    // { audioMuted, videoMuted } — consumed by RoomEvent.Connected
    };
  },

  computed: {
    ...mapGetters('video', [
      'connectionStatus',
      'connectingLabel',
      'error',
      'peerId',
      'sessionId',
      'participantId',
      'isAdmin',
      'localAudioMuted',
      'localVideoMuted',
      'mutedByAdmin',
      'isRecording',
      'remoteParticipants',
      'activeSpeakers',
      'isScreenSharing',
      'screenShareParticipants',
      'meetingStartedAt',
      'totalPeers',
      'pinnedKey',
    ]),

    roomId() {
      return this.$route.params.roomId || '';
    },

    defaultName() {
      return this.$route.query.name || this.$store.getters.fullName || '';
    },

    gridTileCount() {
      return this.totalPeers + this.screenShareParticipants.length;
    },

    gridClass() {
      if (this.pinnedKey) return 'grid-pinned';
      const n = this.gridTileCount;
      if (n <= 1) return 'grid-1';
      if (n === 2) return 'grid-2';
      if (n === 3) return 'grid-3';
      if (n === 4) return 'grid-4';
      if (n <= 6) return 'grid-6';
      return 'grid-many';
    },

    remoteTiles() {
      return Object.entries(this.remoteParticipants).map(([identity, meta]) => ({
        identity,
        name: meta.name,
        audioMuted: meta.audioMuted,
        videoMuted: meta.videoMuted,
        stream: this.remoteMediaStreams[identity] || null,
        isPinned: identity === this.pinnedKey,
      }));
    },

    pinnedTile() {
      if (!this.pinnedKey) return null;
      if (this.pinnedKey.startsWith(IDENTITY_PREFIXES.SCREEN_SHARE)) {
        const identity = this.pinnedKey.slice(IDENTITY_PREFIXES.SCREEN_SHARE.length);
        return {
          type: 'screen',
          identity,
          stream: this.screenShareStreams[identity] || null,
          name: this.screenShareName(identity),
        };
      }
      const meta = this.remoteParticipants[this.pinnedKey];
      if (!meta) return null;
      return {
        type: 'video',
        identity: this.pinnedKey,
        name: meta.name,
        audioMuted: meta.audioMuted,
        videoMuted: meta.videoMuted,
        stream: this.remoteMediaStreams[this.pinnedKey] || null,
      };
    },

    participantList() {
      const local = [{
        identity: this.peerId,
        name: this.localName,
        audioMuted: this.localAudioMuted,
        mutedByAdmin: this.mutedByAdmin,
        isAdmin: this.isAdmin,
        isLocal: true,
      }];
      const remotes = Object.entries(this.remoteParticipants).map(([identity, meta]) => ({
        identity,
        name: meta.name,
        audioMuted: meta.audioMuted,
        mutedByAdmin: meta.mutedByAdmin,
        isAdmin: false,
        isLocal: false,
      }));
      return [...local, ...remotes];
    },

    shareLink() {
      return `${window.location.origin}/#/video-call/${this.roomId}`;
    },

    waitingGuests() {
      return Object.values(this.$store.getters['video/waitingGuests']);
    },

    waitingGuestCount() {
      return this.$store.getters['video/waitingGuestCount'];
    },
  },

  async created() {
    if (!this.roomId) return;
    try {
      const isLoggedIn = this.$store.getters.loggedIn;
      const res = isLoggedIn
        ? await VideoService.getRoomStatus(this.roomId)
        : await VideoService.getPublicRoomStatus(this.roomId);
      const { roomStatus, sessionStatus } = res.data;
      const canJoin = isLoggedIn
        ? roomStatus === ROOM_STATUSES.ACTIVE
        : roomStatus === ROOM_STATUSES.ACTIVE && sessionStatus === ROOM_STATUSES.ACTIVE;
      if (!canJoin) this.phase = VIDEO_PHASES.ENDED;
    } catch (_) {
      // Best-effort: if the status check fails, proceed to lobby normally
    }

    // When a patient guest requests to join, open the waiting room panel for admin
    EventBus.$on('videoGuestRequest', () => {
      if (this.phase === VIDEO_PHASES.CONNECTED && this.isAdmin) {
        this.showWaitingRoom = true;
      }
    });
  },

  beforeUnmount() {
    EventBus.$off('videoGuestRequest');
    this.cleanup();
  },

  methods: {
    // ── Speaker device ───────────────────────────────────────────────────────
    changeSpeaker(deviceId) {
      this.previousSpeakerDeviceId = this.speakerDeviceId;
      this.speakerDeviceId = deviceId;
    },

    // Fired by a VideoTile when setSinkId() rejects for the currently selected
    // device. Ignore stale errors from a device we've already moved on from.
    handleSinkIdError(failedDeviceId) {
      if (failedDeviceId !== this.speakerDeviceId) return;
      this.speakerDeviceId = this.previousSpeakerDeviceId;
      this.speakerErrorSnackbar = true;
    },

    // ── Join flow ────────────────────────────────────────────────────────────
    async handleJoin({ participantName, audioDeviceId, videoDeviceId, speakerDeviceId, audioMuted, videoMuted, role, patientPhone, patientEmail }) {
      this.phase = VIDEO_PHASES.CONNECTING;
      this.localName = participantName;
      this.speakerDeviceId = speakerDeviceId;
      this.pendingJoinOptions = { audioDeviceId, videoDeviceId, audioMuted, videoMuted };

      try {
        // Fetch token — authenticated or guest based on login state
        let tokenData;
        const loggedIn = this.$store.getters.loggedIn;
        if (loggedIn) {
          tokenData = await this.$store.dispatch('video/fetchToken', {
            roomName: this.roomId,
            participantName,
            convoId: this.$route.query.convoId || null,
          });
        } else {
          tokenData = await this.$store.dispatch('video/fetchGuestToken', {
            roomName: this.roomId,
            displayName: participantName,
          });
          // Patient flow: notify admin via backend WebSocket after getting token
          if (role === PARTICIPANT_ROLES.PATIENT && tokenData?.sessionId) {
            await VideoService.patientRequest({
              sessionId: tokenData.sessionId,
              identity: tokenData.peerId,
              displayName: participantName,
              phone: patientPhone || undefined,
              email: patientEmail || undefined,
            });
          }
        }
        console.log('Received token data:', tokenData);

        if (!tokenData?.wsUrl || !tokenData?.token) {
          throw new Error(
            'Server did not return a connection URL — check backend LiveKit configuration (livekit.wsUrl)'
          );
        }

        // Create LiveKit Room (markRaw — must NOT enter Vue reactivity)
        const room = markRaw(new Room({
          adaptiveStream: true,
          dynacast: true,
          audioCaptureDefaults: audioDeviceId ? { deviceId: audioDeviceId } : undefined,
          videoCaptureDefaults: videoDeviceId ? { deviceId: videoDeviceId } : undefined,
        }));
        this.room = room;

        // Store publish preferences — consumed inside RoomEvent.Connected
        this._publishPending = { audioMuted, videoMuted };

        // Wire all LiveKit events before connecting
        this.wireRoomEvents(room);

        // Guest waits for host admission before entering the connected phase
        if (tokenData.waitingForAdmission) {
          this.$store.commit('video/SET_WAITING_FOR_ADMISSION', true);
        }

        // Connect to LiveKit server — 20 s timeout guard
        const connectTimeout = new Promise((_, reject) =>
          setTimeout(() => reject(new Error('Connection timed out — check LiveKit server reachability')), 20000)
        );
        await Promise.race([room.connect(tokenData.wsUrl, tokenData.token), connectTimeout]);

        // If guest is waiting for admission, set the waiting phase.
        // RoomEvent.Connected fires immediately, so we override phase here after connect resolves.
        if (tokenData.waitingForAdmission) {
          this.phase = VIDEO_PHASES.WAITING;
        }
        // For authenticated users, RoomEvent.Connected is the single source that sets phase = 'connected'
        // and triggers camera/mic publishing (see wireRoomEvents).
      } catch (err) {
        console.error('handleJoin error:', err);
        if (err?.response?.data?.meetingEnded) {
          this.phase = VIDEO_PHASES.ENDED;
        } else {
          this.$store.commit('video/SET_ERROR', err?.message || 'Failed to join meeting');
          this.phase = VIDEO_PHASES.ERROR;
        }
      }
    },

    // ── LiveKit event wiring ──────────────────────────────────────────────────
    wireRoomEvents(room) {
      // When the local participant's permissions are upgraded (admitted by host)
      room.localParticipant.on(ParticipantEvent.ParticipantPermissionsChanged, async () => {
        const perms = room.localParticipant.permissions;
        if (perms && perms.canPublish && this.$store.getters['video/waitingForAdmission']) {
          this.$store.commit('video/SET_WAITING_FOR_ADMISSION', false);
          this.$store.commit('video/SET_CONNECTION_STATUS', CONNECTION_STATUS.CONNECTED);
          this.$store.commit('video/SET_MEETING_STARTED_AT', Date.now());
          this.phase = VIDEO_PHASES.CONNECTED;

          // Pre-create empty MediaStream entries for all current remote participants
          // BEFORE publishing so TrackSubscribed events have a target to populate
          room.remoteParticipants.forEach((participant) => {
            if (this.isGuestWaiting(participant)) return;
            if (this.remoteMediaStreams[participant.identity]) return;
            this.$store.commit('video/ADD_REMOTE_PARTICIPANT', {
              identity: participant.identity,
              name: participant.name || participant.identity,
            });
            this.remoteMediaStreams = {
              ...this.remoteMediaStreams,
              [participant.identity]: markRaw(new MediaStream()),
            };
          });

          // Now publish camera and mic
          const opts = this._publishPending || {};
          this._publishPending = null;
          await room.localParticipant.enableCameraAndMicrophone();
          if (opts.audioMuted) await room.localParticipant.setMicrophoneEnabled(false);
          if (opts.videoMuted) await room.localParticipant.setCameraEnabled(false);
          this.$store.commit('video/SET_LOCAL_AUDIO_MUTED', opts.audioMuted || false);
          this.$store.commit('video/SET_LOCAL_VIDEO_MUTED', opts.videoMuted || false);
          this.refreshLocalStream();
        }
      });

      room.on(RoomEvent.ParticipantConnected, (participant) => {
        if (this.isGuestWaiting(participant)) {
          this.$store.commit('video/ADD_WAITING_GUEST', {
            identity: participant.identity,
            name: participant.name || participant.identity,
          });
          if (this.isAdmin) this.showWaitingRoom = true;
          return;
        }
        this.$store.commit('video/ADD_REMOTE_PARTICIPANT', {
          identity: participant.identity,
          name: participant.name || participant.identity,
        });
        this.remoteMediaStreams = {
          ...this.remoteMediaStreams,
          [participant.identity]: markRaw(new MediaStream()),
        };
        // If host is already recording, tell the new participant
        if (this.isAdmin && this.isRecording) {
          this._broadcastRecordingState(true);
        }
      });

      // Sync recording state to all participants via LiveKit data messages
      room.on(RoomEvent.DataReceived, (data) => {
        try {
          const msg = JSON.parse(new TextDecoder().decode(data));
          if (msg.type === DATA_MESSAGE_TYPES.RECORDING_STATE) {
            this.$store.commit('video/SET_RECORDING', {
              isRecording: msg.isRecording,
              egressId: msg.isRecording ? (this.$store.getters['video/egressId'] || null) : null,
            });
          }
        } catch {
          // ignore non-JSON data messages
        }
      });

      room.on(RoomEvent.ParticipantPermissionsChanged, (participantOrPrev, maybeParticipant) => {
        // livekit-client versions differ: some emit (participant, prevPerms), others emit (prevPerms, participant)
        const participant = maybeParticipant && maybeParticipant.identity
          ? maybeParticipant
          : (participantOrPrev && participantOrPrev.identity ? participantOrPrev : null);
        if (!participant) return;

        const perms = participant.permissions;
        const wasWaiting = this.$store.getters['video/waitingGuests'][participant.identity];
        if (wasWaiting && perms && perms.canPublish && !this.isGuestWaiting(participant)) {
          // Guest was admitted — move from waiting to participants
          this.$store.commit('video/REMOVE_WAITING_GUEST', participant.identity);
          this.$store.commit('video/ADD_REMOTE_PARTICIPANT', {
            identity: participant.identity,
            name: participant.name || participant.identity,
          });
          this.remoteMediaStreams = {
            ...this.remoteMediaStreams,
            [participant.identity]: markRaw(new MediaStream()),
          };
        }
      });

      room.on(RoomEvent.ParticipantDisconnected, (participant) => {
        // Remove from waiting list if they were waiting
        this.$store.commit('video/REMOVE_WAITING_GUEST', participant.identity);
        this.$store.commit('video/REMOVE_REMOTE_PARTICIPANT', participant.identity);
        const updated = { ...this.remoteMediaStreams };
        delete updated[participant.identity];
        this.remoteMediaStreams = updated;

        const ssUpdated = { ...this.screenShareStreams };
        delete ssUpdated[participant.identity];
        this.screenShareStreams = ssUpdated;

        const current = this.$store.getters['video/screenShareParticipants'];
        this.$store.commit('video/SET_SCREEN_SHARE_PARTICIPANTS',
          current.filter((id) => id !== participant.identity));
      });

      room.on(RoomEvent.TrackSubscribed, (track, _pub, participant) => {
        if (track.source === Track.Source.ScreenShare) {
          this.screenShareStreams = {
            ...this.screenShareStreams,
            [participant.identity]: markRaw(new MediaStream([track.mediaStreamTrack])),
          };
          const current = this.$store.getters['video/screenShareParticipants'];
          if (!current.includes(participant.identity)) {
            this.$store.commit('video/SET_SCREEN_SHARE_PARTICIPANTS',
              [...current, participant.identity]);
          }
          return;
        }
        let existing = this.remoteMediaStreams[participant.identity];
        if (!existing) {
          // Defensive: TrackSubscribed fired before hydration loop — create entry now
          this.$store.commit('video/ADD_REMOTE_PARTICIPANT', {
            identity: participant.identity,
            name: participant.name || participant.identity,
          });
          existing = markRaw(new MediaStream());
        }
        const updatedStream = markRaw(new MediaStream([...existing.getTracks(), track.mediaStreamTrack]));
        this.remoteMediaStreams = { ...this.remoteMediaStreams, [participant.identity]: updatedStream };

        // Camera track arriving means video is now live
        if (track.source === Track.Source.Camera) {
          this.$store.commit('video/UPDATE_REMOTE_PARTICIPANT_VIDEO', {
            identity: participant.identity, videoMuted: false,
          });
        }
      });

      room.on(RoomEvent.TrackUnsubscribed, (track, _pub, participant) => {
        if (track.source === Track.Source.ScreenShare) {
          const updated = { ...this.screenShareStreams };
          delete updated[participant.identity];
          this.screenShareStreams = updated;
          const current = this.$store.getters['video/screenShareParticipants'];
          this.$store.commit('video/SET_SCREEN_SHARE_PARTICIPANTS',
            current.filter((id) => id !== participant.identity));
          return;
        }
        const existing = this.remoteMediaStreams[participant.identity];
        if (existing) {
          const updatedStream = markRaw(new MediaStream(
            existing.getTracks().filter((t) => t !== track.mediaStreamTrack),
          ));
          this.remoteMediaStreams = { ...this.remoteMediaStreams, [participant.identity]: updatedStream };
        }
        // Camera track gone means video is off
        if (track.source === Track.Source.Camera) {
          this.$store.commit('video/UPDATE_REMOTE_PARTICIPANT_VIDEO', {
            identity: participant.identity, videoMuted: true,
          });
        }
      });

      // LiveKit v2 passes (publication, participant) — use participant directly
      // instead of searching by trackSid, which can fail for guest participants.
      room.on(RoomEvent.TrackMuted, (pub, participant) => {
        const isLocal = participant.identity === room.localParticipant.identity;

        if (isLocal) {
          // Detect server-side admin mute on local microphone
          if (pub.source === Track.Source.Microphone) {
            // Only flag as admin-muted if we didn't initiate the mute ourselves
            if (!this.$store.getters['video/localAudioMuted']) {
              this.$store.commit('video/SET_MUTED_BY_ADMIN', true);
            }
          }
          return;
        }

        if (pub.source === Track.Source.Camera) {
          this.$store.commit('video/UPDATE_REMOTE_PARTICIPANT_VIDEO', {
            identity: participant.identity, videoMuted: true,
          });
        } else if (pub.source === Track.Source.Microphone) {
          this.$store.commit('video/UPDATE_REMOTE_PARTICIPANT_MUTE', {
            identity: participant.identity, audioMuted: true,
          });
        }
      });

      room.on(RoomEvent.TrackUnmuted, (pub, participant) => {
        const isLocal = participant.identity === room.localParticipant.identity;
        if (isLocal) return; // local unmute handled via UI actions

        if (pub.source === Track.Source.Camera) {
          this.$store.commit('video/UPDATE_REMOTE_PARTICIPANT_VIDEO', {
            identity: participant.identity, videoMuted: false,
          });
        } else if (pub.source === Track.Source.Microphone) {
          this.$store.commit('video/UPDATE_REMOTE_PARTICIPANT_MUTE', {
            identity: participant.identity, audioMuted: false,
          });
        }
      });

      room.on(RoomEvent.ActiveSpeakersChanged, (speakers) => {
        this.$store.commit('video/SET_ACTIVE_SPEAKERS', speakers.map((s) => s.identity));
      });

      room.on(RoomEvent.Connected, async () => {
        this.$store.commit('video/SET_CONNECTION_STATUS', CONNECTION_STATUS.CONNECTED);
        this.$store.commit('video/SET_MEETING_STARTED_AT', Date.now());

        // Skip publishing if this is a guest still waiting for admission
        if (this.$store.getters['video/waitingForAdmission']) return;

        this.phase = VIDEO_PHASES.CONNECTED;

        // Publish local tracks using preferences captured in handleJoin
        const opts = this._publishPending || {};
        this._publishPending = null;
        await room.localParticipant.enableCameraAndMicrophone();
        if (opts.audioMuted) await room.localParticipant.setMicrophoneEnabled(false);
        if (opts.videoMuted) await room.localParticipant.setCameraEnabled(false);
        this.$store.commit('video/SET_LOCAL_AUDIO_MUTED', opts.audioMuted || false);
        this.$store.commit('video/SET_LOCAL_VIDEO_MUTED', opts.videoMuted || false);
        this.refreshLocalStream();

        // Hydrate pre-existing remote participants
        this._hydrateRemoteParticipants(room);
      });

      room.on(RoomEvent.Reconnecting, () => {
        this.$store.commit('video/SET_CONNECTION_STATUS', CONNECTION_STATUS.RECONNECTING);
        this.phase = VIDEO_PHASES.RECONNECTING;
      });

      room.on(RoomEvent.Reconnected, () => {
        this.$store.commit('video/SET_CONNECTION_STATUS', CONNECTION_STATUS.CONNECTED);
        this.phase = VIDEO_PHASES.CONNECTED;
      });

      room.on(RoomEvent.Disconnected, (reason) => {
        this.$store.commit('video/SET_CONNECTION_STATUS', CONNECTION_STATUS.DISCONNECTED);
        const wasWaiting = this.$store.getters['video/waitingForAdmission'];
        const sessionId = this.$store.getters['video/sessionId'];
        this.cleanup();
        if (reason === DisconnectReason.ROOM_DELETED) {
          const isLoggedIn = this.$store.getters.loggedIn;
          if (isLoggedIn) {
            this.tryCloseTab();
          } else {
            this.$router.push({ path: ROUTE_PATHS.MEETING_ENDED, query: { sessionId } });
          }
        } else if (wasWaiting) {
          // Guest was removed while waiting — they were denied
          this.phase = VIDEO_PHASES.DENIED;
        } else {
          this.goBack();
        }
      });
    },

    // ── Media controls ────────────────────────────────────────────────────────
    async toggleAudio() {
      const lp = this.room?.localParticipant;
      if (!lp) return;
      const muted = !this.localAudioMuted;
      await lp.setMicrophoneEnabled(!muted);
      this.$store.commit('video/SET_LOCAL_AUDIO_MUTED', muted);
    },

    async toggleVideo() {
      const lp = this.room?.localParticipant;
      if (!lp) return;
      const muted = !this.localVideoMuted;
      await lp.setCameraEnabled(!muted);
      this.$store.commit('video/SET_LOCAL_VIDEO_MUTED', muted);
      if (!muted) this.refreshLocalStream();
    },

    async toggleScreenShare() {
      const lp = this.room?.localParticipant;
      if (!lp) return;
      if (this.isScreenSharing) {
        await lp.setScreenShareEnabled(false);
        const updated = { ...this.screenShareStreams };
        delete updated[this.peerId];
        this.screenShareStreams = updated;
        const current = this.$store.getters['video/screenShareParticipants'];
        this.$store.commit('video/SET_SCREEN_SHARE_PARTICIPANTS',
          current.filter((id) => id !== this.peerId));
        this.$store.commit('video/SET_IS_SCREEN_SHARING', false);
      } else {
        try {
          await lp.setScreenShareEnabled(true);
          const pub = lp.getTrackPublication(Track.Source.ScreenShare);
          if (pub?.videoTrack) {
            this.screenShareStreams = {
              ...this.screenShareStreams,
              [this.peerId]: markRaw(new MediaStream([pub.videoTrack.mediaStreamTrack])),
            };
            const current = this.$store.getters['video/screenShareParticipants'];
            this.$store.commit('video/SET_SCREEN_SHARE_PARTICIPANTS', [...current, this.peerId]);
            this.$store.commit('video/SET_IS_SCREEN_SHARING', true);
          }
        } catch (e) {
          console.warn('Screen share cancelled:', e);
        }
      }
    },

    async toggleRecording() {
      if (!this.isAdmin) return;
      if (this.isRecording) {
        await this.$store.dispatch('video/stopRecording');
        this._broadcastRecordingState(false);
      } else {
        await this.$store.dispatch('video/startRecording', this.roomId);
        this._broadcastRecordingState(true);
      }
    },

    _broadcastRecordingState(isRecording) {
      if (!this.room?.localParticipant) return;
      const msg = JSON.stringify({ type: DATA_MESSAGE_TYPES.RECORDING_STATE, isRecording });
      const encoded = new TextEncoder().encode(msg);
      this.room.localParticipant.publishData(encoded, { reliable: true });
    },

    // ── Admin controls ────────────────────────────────────────────────────────
    async onAdminMute(identity) {
      await this.$store.dispatch('video/adminMute', {
        sessionId: this.sessionId,
        peerId: identity,
      });
    },

    async onAdminKick(identity) {
      await this.$store.dispatch('video/adminKick', {
        sessionId: this.sessionId,
        peerId: identity,
      });
    },

    // ── Helpers ───────────────────────────────────────────────────────────────
    // A guest is in waiting state if their identity starts with 'guest-'
    // and they cannot publish (permissions may be undefined on first connect)
    isGuestWaiting(participant) {
      if (!participant.identity.startsWith(IDENTITY_PREFIXES.GUEST)) return false;
      const perms = participant.permissions;
      // If permissions not yet loaded, trust the identity prefix
      if (!perms) return true;
      return !perms.canPublish;
    },

    // ── Pin / Focus ───────────────────────────────────────────────────────────
    onPin(key) {
      this.$store.commit('video/SET_PINNED_KEY', key);
    },

    // ── Waiting room controls ─────────────────────────────────────────────────
    async onAdmitGuest(identity) {
      await this.$store.dispatch('video/admitGuest', { roomName: this.roomId, identity });
    },

    async onDenyGuest(identity) {
      await this.$store.dispatch('video/denyGuest', { roomName: this.roomId, identity });
    },

    // ── Remote participant hydration ─────────────────────────────────────────
    _hydrateRemoteParticipants(room) {
      room.remoteParticipants.forEach((participant) => {
        if (this.isGuestWaiting(participant)) {
          this.$store.commit('video/ADD_WAITING_GUEST', {
            identity: participant.identity,
            name: participant.name || participant.identity,
          });
          return;
        }
        this.$store.commit('video/ADD_REMOTE_PARTICIPANT', {
          identity: participant.identity,
          name: participant.name || participant.identity,
        });
        // Sync actual mute state from LiveKit publications (covers reconnect and host-muted guests)
        let audioMuted = false;
        participant.trackPublications.forEach((pub) => {
          if (pub.source === Track.Source.Microphone && pub.isMuted) audioMuted = true;
        });
        if (audioMuted) {
          this.$store.commit('video/UPDATE_REMOTE_PARTICIPANT_MUTE', {
            identity: participant.identity, audioMuted: true,
          });
        }
        const ms = markRaw(new MediaStream());
        participant.trackPublications.forEach((pub) => {
          if (!pub.track) return;
          if (pub.source === Track.Source.ScreenShare) {
            this.screenShareStreams = {
              ...this.screenShareStreams,
              [participant.identity]: markRaw(new MediaStream([pub.track.mediaStreamTrack])),
            };
            const current = this.$store.getters['video/screenShareParticipants'];
            if (!current.includes(participant.identity)) {
              this.$store.commit('video/SET_SCREEN_SHARE_PARTICIPANTS',
                [...current, participant.identity]);
            }
          } else {
            ms.addTrack(pub.track.mediaStreamTrack);
          }
        });
        this.remoteMediaStreams = { ...this.remoteMediaStreams, [participant.identity]: ms };
      });
    },

    // ── Leave / End ───────────────────────────────────────────────────────────
    async leaveCall() {
      if (this.isAdmin && this.isRecording) {
        this.showRecordingLeaveAlert = true;
        return;
      }
      const isGuest = !this.$store.getters.loggedIn;
      await this.$store.dispatch('video/recordLeave', { isGuest });
      this.cleanup();
      this.tryCloseTab();
    },

    async endMeeting() {
      try {
        await this.$store.dispatch('video/endMeeting');
        // LiveKit fires Disconnected(ROOM_DELETED) → handled by wireRoomEvents
      } catch (e) {
        this.cleanup();
        this.tryCloseTab();
      }
    },

    // ── Helpers ───────────────────────────────────────────────────────────────
    refreshLocalStream() {
      const lp = this.room?.localParticipant;
      if (!lp) return;
      const camPub = lp.getTrackPublication(Track.Source.Camera);
      if (camPub?.videoTrack) {
        this.localStream = markRaw(new MediaStream([camPub.videoTrack.mediaStreamTrack]));
      }
    },

    screenShareName(identity) {
      if (identity === this.peerId) return `${this.localName} (screen)`;
      const meta = this.remoteParticipants[identity];
      return meta ? `${meta.name} (screen)` : 'Screen share';
    },

    cleanup() {
      if (this.room) {
        this.room.disconnect();
        this.room = null;
      }
      if (this.localStream) {
        this.localStream.getTracks().forEach((t) => t.stop());
        this.localStream = null;
      }
      this.remoteMediaStreams = {};
      this.screenShareStreams = {};
      this.$store.commit('video/RESET');
    },

    tryCloseTab() {
      if (window.history.length <= 2 || window.opener !== null) {
        window.close();
      } else {
        this.goBack();
      }
    },

    goBack() {
      if (this.$store.getters.loggedIn) {
        this.$router.push(ROUTE_PATHS.CONVERSATION);
      } else {
        this.$router.push(ROUTE_PATHS.MEETING_ENDED);
      }
    },
  },
};
</script>

<style scoped>
/* Full-screen dark container — overrides any App.vue padding */
.meet-room {
  display: flex;
  flex-direction: column;
  height: 100vh;
  width: 100%;
  background: #111;
  overflow: hidden;
  position: fixed;
  inset: 0;
  z-index: 100;
}

/* ── Overlays ──────────────────────────────────────────────────────────────── */
.meet-overlay {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  color: #e0e0e0;
  background: #111;
}

.meet-overlay__label {
  font-size: 1.1rem;
  font-weight: 500;
  color: #e0e0e0;
  margin: 0;
}

.meet-overlay__sub {
  font-size: 0.85rem;
  color: #9e9e9e;
  margin: 0;
}

/* ── Video grid ────────────────────────────────────────────────────────────── */
.meet-grid {
  flex: 1;
  display: grid;
  background: #1a1a1a;
  gap: 4px;
  padding: 4px;
  min-height: 0;
}

.meet-grid.grid-1  { grid-template-columns: 1fr; }
.meet-grid.grid-2  { grid-template-columns: 1fr 1fr; }
.meet-grid.grid-3  { grid-template-columns: 1fr 1fr; grid-template-rows: 1fr 1fr; }
.meet-grid.grid-3 :deep(.video-tile:first-child) { grid-column: 1 / -1; }
.meet-grid.grid-4  { grid-template-columns: 1fr 1fr; grid-template-rows: 1fr 1fr; }
.meet-grid.grid-6  { grid-template-columns: repeat(3, 1fr); }
.meet-grid.grid-many { grid-template-columns: repeat(auto-fill, minmax(280px, 1fr)); }

/* Pinned layout: flex two-zone (main + sidebar strip) */
.meet-pinned-layout {
  flex: 1;
  display: flex;
  min-height: 0;
  background: #1a1a1a;
  gap: 4px;
  padding: 4px;
}

.pinned-main {
  flex: 1;
  min-width: 0;
  min-height: 0;
}

.pinned-main :deep(.video-tile) {
  height: 100%;
  width: 100%;
}

.pinned-sidebar {
  width: 180px;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
  overflow-y: auto;
}

.pinned-sidebar :deep(.video-tile) {
  height: 120px;
  flex-shrink: 0;
}
/* ── Waiting placeholder ───────────────────────────────────────────────────── */
.meet-waiting {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  color: #616161;
  grid-column: 1 / -1;
  padding: 24px;
  align-self: center;
  justify-self: center;
}

.meet-waiting p { margin: 0; font-size: 0.9rem; }
.meet-waiting__hint { font-size: 0.78rem !important; color: #757575; word-break: break-all; text-align: center; }
</style>
